/**
 * reCAPTCHA v3 loader — idempotent. Loads the Google script once and returns
 * a token for a named action. Backend must verify success + action + score.
 */
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.grecaptcha && typeof window.grecaptcha.execute === "function") {
    return Promise.resolve();
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-recaptcha="v3"]');
    if (existing) {
      const wait = () => {
        if (window.grecaptcha && typeof window.grecaptcha.execute === "function") resolve();
        else setTimeout(wait, 40);
      };
      wait();
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = "v3";
    s.onload = () => {
      if (window.grecaptcha) resolve();
      else reject(new Error("reCAPTCHA script loaded without grecaptcha"));
    };
    s.onerror = () => reject(new Error("Could not load reCAPTCHA"));
    document.head.appendChild(s);
  });

  return scriptPromise;
}

export function isRecaptchaConfigured() {
  return Boolean(SITE_KEY);
}

/** Return a fresh reCAPTCHA v3 token for `action`. Never reuse a token. */
export async function getRecaptchaToken(action) {
  if (!SITE_KEY) return null; // no-op in dev when key missing
  await loadScript();
  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(SITE_KEY, { action })
        .then(resolve)
        .catch(reject);
    });
  });
}
